import Animated, { clamp, interpolate, useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { View } from 'react-native';
import { useEffect, useRef } from 'react';
import { styles, stylesCustom } from './Slider.styles';
import { SLIDER_CROP_MAX_VALUE, SLIDER_DOT_CROP_WIDTH, SLIDER_DOT_WIDTH, SLIDER_MAX_VALUE } from './Slider.constants';
import { BaseButton } from '@photosi/rn-components/buttons/base-button/BaseButton';
import { generateTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { brand } from 'src/theme/colors';
import { SliderProps } from './Slider.types';

export const Slider = ({
  minValue = 0.1,
  maxValue = 2,
  defaultValue,
  onChange,
  onChangeEnd,
  testID,
  focusId,
  focusOrder,
  focusGroup,
  isDark = false,
  customWidth = false,
  accessible = true,
  accessibilityLabel,
  accessibilityRole,
}: SliderProps) => {
  const SliderMaxValue = customWidth ? SLIDER_CROP_MAX_VALUE : SLIDER_MAX_VALUE;
  const SliderDotWidth = customWidth ? SLIDER_DOT_CROP_WIDTH : SLIDER_DOT_WIDTH;
  const sliderSV = useSharedValue(
    clamp(interpolate(defaultValue, [minValue, maxValue], [0, SliderMaxValue]), 0, SliderMaxValue),
  );

  const customDarkColor = isDark ? '#252527' : brand.main.water;

  // Tracks if gesture started over the indicator
  const startedOnHandle = useRef(false);

  // Flag to track if we're actually moving the slider
  const isMoving = useRef(false);

  useEffect(() => {
    sliderSV.value = clamp(interpolate(defaultValue, [minValue, maxValue], [0, SliderMaxValue]), 0, SliderMaxValue);
  }, [defaultValue]);

  const sliderStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: sliderSV.value }],
      backgroundColor: customDarkColor,
    };
  });

  const sliderTrackStyle = useAnimatedStyle(() => {
    return {
      width: sliderSV.value + SliderDotWidth / 2,
      backgroundColor: customDarkColor,
    };
  });

  // Tracks if x position is over the indicator
  const isPositionOverHandle = (x: number, handlePosition: number) => {
    'worklet';
    const handleStart = handlePosition;
    const handleEnd = handlePosition + SliderDotWidth;
    return x >= handleStart && x <= handleEnd;
  };

  // Tracks if position x is on the center of the indicator
  const isPositionAtHandleCenter = (x: number, handlePosition: number) => {
    'worklet';
    const handleCenter = handlePosition + SliderDotWidth / 2;
    const tolerance = 5; // Slight tolerance over indicator center
    return Math.abs(x - handleCenter) <= tolerance;
  };

  // Tracks if position x is 'inside' slider width area
  const isPositionInSliderArea = (x: number) => {
    'worklet';
    return x >= 0 && x <= SliderMaxValue + SliderDotWidth;
  };

  const panSlider = Gesture.Pan()
    .minDistance(1)
    .onStart(event => {
      const handlePosition = sliderSV.value;
      startedOnHandle.current = isPositionOverHandle(event.x, handlePosition);

      if (startedOnHandle.current) {
        isMoving.current = true;
      }
    })
    .onChange(event => {
      if (!startedOnHandle.current) return;

      if (isMoving.current) {
        const isInSliderArea = isPositionInSliderArea(event.x);

        if (isInSliderArea) {
          const newPosition = clamp(sliderSV.value + event.changeX, 0, SliderMaxValue);
          sliderSV.value = newPosition;

          const value = interpolate(sliderSV.value, [0, SliderMaxValue], [minValue, maxValue]);
          onChange(value);
        } else {
          isMoving.current = false;
        }
      } else {
        const handlePosition = sliderSV.value;
        const isCentered = isPositionAtHandleCenter(event.x, handlePosition);

        if (isCentered) {
          isMoving.current = true;
        }
      }
    })
    .onEnd(event => {
      if (startedOnHandle.current) {
        const value = interpolate(sliderSV.value, [0, SliderMaxValue], [minValue, maxValue]);
        onChangeEnd?.(value);
      }
      startedOnHandle.current = false;
      isMoving.current = false;
    });

  const tap = Gesture.Tap().onEnd(event => {
    sliderSV.value = clamp(event.x - SliderDotWidth / 2, 0, SliderMaxValue);

    const value = interpolate(sliderSV.value, [0, SliderMaxValue], [minValue, maxValue]);
    onChangeEnd?.(value);
  });
  const style = customWidth ? stylesCustom : styles;
  return (
    <GestureDetector gesture={Gesture.Race(panSlider, tap)}>
      <View style={style.sliderContainer}>
        <View style={[style.sliderTrack]} testID={testID ?? 'slider'}>
          <Animated.View style={[style.sliderHandle, sliderStyle]}>
            <BaseButton
              style={[style.button, { width: SliderDotWidth + 8, height: SliderDotWidth + 8 }]}
              testID={generateTestID(testID, TestIDs.SLIDER_DOT)}
              focusStyle={style.buttonFocus}
              focusId={focusId}
              focusOrder={focusOrder}
              focusGroup={focusGroup}
              onKeyDown={async (event: KeyboardEvent) => {
                if (event.code === 'ArrowRight' || event.code === 'ArrowLeft') {
                  event.preventDefault();
                  event.stopPropagation();
                  sliderSV.value = event.code === 'ArrowRight' ? sliderSV.value + 1 : sliderSV.value - 1;
                  const value = interpolate(sliderSV.value, [0, SliderMaxValue], [minValue, maxValue]);
                  onChangeEnd && onChangeEnd(value);
                }
              }}
              accessible={accessible}
              accessibilityLabel={accessibilityLabel ?? `slider ${focusId ?? testID}`}
              accessibilityRole={accessibilityRole ?? 'slider'}
            >
              {() => <></>}
            </BaseButton>
          </Animated.View>
          <Animated.View style={[style.sliderTrack, style.sliderTrackActive, sliderTrackStyle]} />
        </View>
      </View>
    </GestureDetector>
  );
};
